import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, X, Filter, Search } from 'lucide-react';
import { useThemeStore } from '../../state/theme';

export interface SidebarFilters {
  industry: string[];
  language: string[];
  applicationStatus: string[];
  screeningStatus: string[];
}

interface FilterSection {
  key: keyof SidebarFilters;
  label: string;
  options: { value: string; label: string; count?: number }[];
  searchable?: boolean;
}

interface FilterSidebarProps {
  filters: SidebarFilters;
  onChange: (filters: SidebarFilters) => void;
  sections: FilterSection[];
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  onChange,
  sections,
  isOpen,
  onToggle,
}) => {
  const { isDarkMode } = useThemeStore();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [sectionSearch, setSectionSearch] = useState<Record<string, string>>({});
  const [width, setWidth] = useState(256);
  const isResizing = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.min(Math.max(newWidth, 200), 500));
    };

    const handleMouseUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const toggleSection = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleFilter = (sectionKey: keyof SidebarFilters, value: string) => {
    const current = filters[sectionKey];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...filters, [sectionKey]: updated });
  };

  const clearAll = () => {
    onChange({
      industry: [],
      language: [],
      applicationStatus: [],
      screeningStatus: [],
    });
  };

  const hasActiveFilters = Object.values(filters).some(
    (arr) => arr.length > 0
  );

  const activeCount = Object.values(filters).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  if (!isOpen) return null;

  return (
    <div
      className={`flex-shrink-0 border-l flex flex-col sticky ml-2 top-0 h-[calc(100vh-2rem)] overflow-hidden ${
        isDarkMode
          ? 'border-gray-700 bg-gray-800'
          : 'border-greyFifty bg-white'
      }`}
      style={{ width, minWidth: 280 }}
    >
      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10 hover:bg-blue-500 transition-colors ${
          isDarkMode ? 'hover:bg-blue-400' : 'hover:bg-blue-500'
        }`}
      />
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 pt-3 pb-5 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-greyFifty'
        }`}
      >
        <div className="flex items-center gap-2">
          <Filter size={16} className={isDarkMode ? 'text-gray-300' : 'text-greyNinety'} />
          <span
            className={`text-sm font-semibold ${
              isDarkMode ? 'text-gray-100' : 'text-blackTen'
            }`}
          >
            Filters
          </span>
          {/* {activeCount > 0 && (
            <span className="bg-blue-600 text-white text-xxs px-1.5 py-0.5 rounded-full font-medium">
              {activeCount}
            </span>
          )} */}
        </div>
        <div className="flex items-center gap-1">
          {hasActiveFilters && (
            <button
              onClick={clearAll}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium mr-1"
            >
              Clear all
            </button>
          )}
          <button
            onClick={() => onToggle(false)}
            className={`p-1 rounded hover:bg-opacity-10 ${
              isDarkMode
                ? 'hover:bg-white text-gray-400'
                : 'hover:bg-black text-greyNinety'
            }`}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Filter Sections */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        {sections.map((section) => {
          const isCollapsed = collapsed[section.key] ?? false;
          const selectedCount = filters[section.key].length;

          return (
            <div
              key={section.key}
              className={`border-b ${
                isDarkMode ? 'border-gray-700' : 'border-greyFifty'
              }`}
            >
              <button
                onClick={() => toggleSection(section.key)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors ${
                  isDarkMode
                    ? 'text-gray-200 hover:bg-gray-700'
                    : 'text-blackFourty hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>{section.label}</span>
                  {selectedCount > 0 && (
                    <span className="bg-blue-50 text-blue-400 text-xxs px-2 rounded-full font-medium">
                      {selectedCount}
                    </span>
                  )}
                </div>
                {isCollapsed ? (
                  <ChevronRight size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>

              {!isCollapsed && (
                <div className="px-4 pb-3 flex flex-col gap-1">
                  {section.searchable && (
                    <div className="relative mb-1 flex-shrink-0">
                      <Search
                        size={14}
                        className={`absolute left-2 top-1/2 -translate-y-1/2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}
                      />
                      <input
                        type="text"
                        placeholder={`Search ${section.label.toLowerCase()}...`}
                        value={sectionSearch[section.key] || ''}
                        onChange={(e) =>
                          setSectionSearch((prev) => ({
                            ...prev,
                            [section.key]: e.target.value,
                          }))
                        }
                        className={`w-full pl-7 pr-2 py-1.5 text-xs rounded border outline-none ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500'
                            : 'bg-white border-gray-200 text-gray-700 placeholder-gray-400'
                        }`}
                      />
                    </div>
                  )}
                  <div>
                    {section.options
                      .filter((option) => {
                        const query = (sectionSearch[section.key] || '').toLowerCase();
                        if (!query) return true;
                        return option.label.toLowerCase().includes(query);
                      })
                      .map((option) => {
                        const isSelected = filters[section.key].includes(
                          option.value
                        );
                        return (
                          <label
                            key={option.value}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors ${
                              isDarkMode
                                ? 'hover:bg-gray-700 text-gray-300'
                                : 'hover:bg-gray-50 text-greyHundred'
                            } ${isSelected ? (isDarkMode ? 'bg-gray-700' : 'bg-blue-50') : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() =>
                                toggleFilter(section.key, option.value)
                              }
                              className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                            <span className="truncate flex-1">{option.label}</span>
                            {option.count !== undefined && (
                              <span className={`text-xxs font-medium ${
                                isDarkMode ? 'text-gray-500' : 'text-greyNinety'
                              }`}>
                                {option.count}
                              </span>
                            )}
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FilterSidebar;
